import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconButton, SvgIcon, Tooltip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { usePopover } from "@hooks/shared/use-popover";
import useFavouritesStore from "@stores/shared/useFavouritesStore";
import useFavouritesCollectionStore from "@stores/shared/useFavouritesCollectionStore";
import useFavouritesUserCollectionStore from "@stores/client/useFavouritesUserCollectionStore";
import { favouriteResourceTypes } from "@components/shared/elements/favourites/components/constants/favourite-resource-types";
import toast from "react-hot-toast";
import { FavouritesPopover } from "./favourites-popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/pro-light-svg-icons";
import { faStar as solidfaStar } from "@fortawesome/pro-solid-svg-icons";

const SolidColoredFaStar = () => {
  const theme = useTheme();
  return (
    <SvgIcon>
      <SvgIcon sx={{ color: theme.palette.brand }}>
        <FontAwesomeIcon className="fa-stack-1x" icon={solidfaStar} />
      </SvgIcon>
      <SvgIcon sx={{ color: theme.palette.brand }}>
        <FontAwesomeIcon className="fa-stack-1x" icon={faStar} />
      </SvgIcon>
    </SvgIcon>
  );
};

export const FavouritesButton = () => {
  const popover = usePopover();
  const location = useLocation();
  const currentPathname = location.pathname;
  const theme = useTheme();
  const { t } = useTranslation();

  const { initializeFavourites, createFavourite, checkIsFavourite } =
    useFavouritesStore();
  const { initializeFavouritesCollection, createCollection } =
    useFavouritesCollectionStore();
  const [isFavourited, setIsFavourited] = useState(false);
  const [isNewFavourite, setIsNewFavourite] = useState(false);
  const [existingFavouriteId, setExistingFavouriteId] = useState(null);
  const { fetchItems: fetchUserCollections } =
    useFavouritesUserCollectionStore();

  useEffect(() => {
    const fetchData = async () => {
      await initializeFavourites("frontend");
      await initializeFavouritesCollection("frontend");
      // helps with checking if a loaded page is a favourite or not
      const result = await checkIsFavourite(currentPathname);
      const favourited = result?.isFavourite ?? !!result;
      setIsFavourited(favourited);
      setExistingFavouriteId(favourited ? (result?.id ?? null) : null);
    };
    fetchData();
  }, [currentPathname]);

  const getFavouriteType = () => {
    const endsWithNumber = /\/\d+$/.test(currentPathname);

    if (!endsWithNumber) {
      return 7;
    }

    const matchedType = favouriteResourceTypes.find((type) => {
      return currentPathname
        .toLowerCase()
        .includes(`/${type.name.toLowerCase()}`);
    });

    return matchedType ? matchedType.key : 7;
  };

  const handleFavouritesButtonClick = async () => {
    // fetching user collection list for the dropdown
    await fetchUserCollections();

    if (isFavourited) {
      setIsNewFavourite(false); // Editing existing favourite
    } else {
      setIsNewFavourite(true); // Mark this as a new favourite
      // Read the freshly-fetched collections from the store. The value
      // destructured at render time is a stale closure and is still empty on
      // the first click after mount (collections load on click, not on mount),
      // so finding "Default" must go through live store state.
      const collections =
        useFavouritesUserCollectionStore.getState().favouritesUserCollections;
      // Find or create Default collection
      let defaultCollection = collections.find(
        (col) => col.name === "Default",
      );

      if (!defaultCollection) {
        // Create Default collection
        const newCollection = {
          name: "Default",
          type: 1,
          icon: "faStar",
          order: 0,
        };

        try {
          const createdCollection = await new Promise((resolve, reject) => {
            createCollection(
              newCollection,
              (result) => resolve(result),
              (error) => reject(error),
            );
          });
          defaultCollection = createdCollection;
          await fetchUserCollections(); // Refresh collections
        } catch (error) {
          toast.error(t("favourites.collectionCreateError"));
          console.error("Error creating collection:", error);
          return;
        }
      }

      // Create favourite in Default collection
      const newFavourite = {
        name: currentPathname || t("favourites.untitledPage"),
        type: getFavouriteType(),
        url: currentPathname,
        order: 0,
        favouriteCollectionID: defaultCollection.id,
      };

      const successCallback = () => {
        toast.success(t("favourites.pageAddedToDefault"));
        setIsFavourited(true);
      };

      const errorCallback = (error) => {
        if (error.status === 409) {
          toast.error(t("favourites.pageAlreadyInFavourites"));
        } else {
          toast.error(t("favourites.favouriteAddError"));
        }
        console.error("Error creating favourite:", error);
      };

      createFavourite(newFavourite, successCallback, errorCallback);
    }

    popover.handleOpen();
  };

  return (
    <>
      <Tooltip title={t("favourites.favourite")}>
        <IconButton
          data-testid="fav-button"
          ref={popover.anchorRef}
          onClick={handleFavouritesButtonClick}
          sx={{
            backgroundColor: popover.open ? alpha(theme.palette.common.white, 0.1) : "transparent",
            color: popover.open ? theme.palette.primary.contrastText : theme.palette.common.white,
            fontWeight: popover.open ? 700 : undefined,
          }}
        >
          {isFavourited ? (
            <SolidColoredFaStar />
          ) : (
            <SvgIcon>
              <FontAwesomeIcon icon={faStar} color="white" />
            </SvgIcon>
          )}
        </IconButton>
      </Tooltip>

      <FavouritesPopover
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
        setIsFavourited={setIsFavourited}
        isNewFavourite={isNewFavourite}
        existingFavouriteId={existingFavouriteId}
      />
    </>
  );
};
